import { config } from './chunk-WHCMK6SD.js';

// ../base/auth/error.ts
var ERROR_CODES = ["MISSING_TOKEN", "MFA_REQUIRED"];
var JwAuthError = class extends Error {
  static CODES = ERROR_CODES;
  name = "JwAuthError";
  code;
  constructor(code) {
    super(code);
    this.code = code;
  }
  toString() {
    return this.code;
  }
};

// ../../node_modules/@jwplayer/fe-common/module/cookies/index.js
function isDefined(v) {
  return !(typeof v === "undefined");
}
function formatCookie(key, value, config2) {
  if (key.trim() === "") {
    throw new Error("Key may not be empty");
  }
  return [
    `${key}=${value}`,
    ...Object.entries(config2 ?? {}).map(([configKey, configValue]) => {
      if (typeof configValue === "undefined" || configValue === null) {
        return;
      }
      if (configValue === true) {
        return configKey;
      }
      if (configValue instanceof Date) {
        configValue = configValue.toUTCString();
      }
      return `${configKey}=${configValue}`;
    })
  ].filter(isDefined).join("; ");
}
function setCookie(key, value, config2) {
  document.cookie = formatCookie(key, value, config2);
}
function deleteCookie(key, config2) {
  document.cookie = formatCookie(key, "", {
    ...config2,
    "Max-Age": -999999
  });
}
function getCookie(key) {
  const cookies = document.cookie.split(";").map((i) => i.trim());
  for (const cookie of cookies) {
    const parts = cookie.split("=");
    if (parts.length < 2) {
      continue;
    }
    const name = parts.shift();
    if (name === key) {
      return parts.join("=");
    }
  }
  return void 0;
}

// ../../node_modules/@jwplayer/fe-common/module/util/promise/index.js
var PromiseUtil = {
  /**
   * Returns a cached promise while it is pending
   *
   * Once the promise is resolved, the cache is cleared and subsequent calls will create a new promise.
   */
  coalesce(fn) {
    let promise;
    return (...args) => {
      if (promise) {
        return promise;
      }
      promise = fn(...args).finally(() => {
        promise = void 0;
      });
      return promise;
    };
  },
  /**
   * Returns a cached promise, ensuring the promise runs only once
   *
   * Unlike memoization, this returns the promise, not the result.
   */
  once(fn) {
    let promise = void 0;
    return (...args) => {
      if (promise) {
        return promise;
      }
      promise = fn(...args);
      return promise;
    };
  }
};

// ../base/auth/manager.ts
var AuthManager = class {
  eventHandlers = {
    authenticated: [],
    refreshFailed: [],
    cleared: []
  };
  refreshTimeout;
  /**
   * Cached 'session' object
   */
  cache;
  opts;
  /**
   * Cached token
   */
  cachedToken;
  constructor(opts) {
    this.opts = {
      ...opts
    };
  }
  resetTimers() {
    clearTimeout(this.refreshTimeout);
  }
  /**
   * Clear the cached session and auth token (cookie).
   */
  clear() {
    this.cachedToken = void 0;
    deleteCookie(this.opts.authCookieName(), {
      Path: "/"
    });
    this.cache = void 0;
    this.resetTimers();
    this.emit("cleared");
  }
  /**
   * Emit event (with arguments)
   */
  emit(event, ...args) {
    this.eventHandlers[event].forEach((h) => h.apply(h, args));
  }
  /**
   * API call needs to be implemented in child class
   */
  async authenticateApi(_opts) {
    throw new Error("Not implemented");
  }
  /**
   * API call needs to be implemented in child class
   */
  async loginApi(_params, _opts) {
    throw new Error("Not implemented");
  }
  /**
   * Returns an existing session if there is one. If not, then an API
   * call is made to refresh the session from an auth cookie.
   */
  async authenticate(opts) {
    const cache = this.getCache();
    if (cache) {
      return cache;
    }
    const token = this.getToken();
    if (!token) {
      throw new JwAuthError("MISSING_TOKEN");
    }
    try {
      const res = await this.authenticateApi(opts);
      this.setCache(res.obj, res.token);
      return res.obj;
    } catch (err) {
      if (!(err instanceof Error && err.name === "AbortError")) {
        this.clear();
      }
      throw err;
    }
  }
  async login(params, opts) {
    const res = await this.loginApi(params, opts);
    this.setCache(res.obj, res.token);
    return res.obj;
  }
  logout() {
    this.clear();
  }
  getToken() {
    if (!this.cachedToken) {
      this.cachedToken = getCookie(this.opts.authCookieName()) || void 0;
    }
    return this.cachedToken;
  }
  getCache() {
    if (!this.cache) {
      return;
    }
    return structuredClone(this.cache);
  }
  /**
   * Set cached session object and auth token
   *
   * Automatically refreshes the session after a timeout, except when `autoRefresh` is false
   *
   * @param cache
   * @param token
   * @param autoRefresh
   */
  setCache(cache, token, autoRefresh = true) {
    this.cache = structuredClone(cache);
    this.cachedToken = token;
    setCookie(this.opts.authCookieName(), token, {
      Path: "/"
    });
    this.emit("authenticated", cache);
    this.resetTimers();
    const authRefreshTime = this.opts.authRefreshTime?.();
    if (autoRefresh && typeof authRefreshTime === "number") {
      this.refreshTimeout = setTimeout(async () => {
        try {
          const { obj, token: token2 } = await this.authenticateApi({});
          this.setCache(obj, token2, true);
        } catch (e) {
          this.clear();
          this.emit("refreshFailed", e);
        }
      }, authRefreshTime);
    }
  }
  /**
   * Subscribe to event
   *
   * Returns function to unsubscribe
   *
   * @param event Event name
   * @param handler Event handler
   */
  subscribe(event, handler) {
    if (!this.eventHandlers[event].includes(handler)) {
      this.eventHandlers[event].push(handler);
    }
    return () => {
      this.eventHandlers[event] = this.eventHandlers[event].filter((h) => h !== handler);
    };
  }
};

// src/auth/util.ts
function extractAuthToken(obj) {
  return obj.authentication.dashboard;
}

// src/auth/index.ts
var AuthGatewayManager = class extends AuthManager {
  constructor() {
    super({
      authCookieName: config.select((c) => c.auth.cookies.token),
      authRefreshTime: config.select((c) => c.auth.autoRefreshTime)
    });
  }
  authenticatedApi = PromiseUtil.coalesce(async (opts) => {
    const { jwApi } = await import('./api-3PLEF6F2.js');
    const obj = (await jwApi("dashboard-scope", "read", {}, opts)).data;
    return {
      obj,
      token: extractAuthToken(obj)
    };
  });
  /**
   * Authenticate a user with the API Gateway.
   *
   * In case MFA is set up for the user, the error `MFA_REQUIRED` is thrown when
   * the user needs to enter their MFA code.
   */
  loginApi = PromiseUtil.coalesce(async ({ email, password, mfaCode, rememberMfa }, opts) => {
    const { jwApi } = await import('./api-3PLEF6F2.js');
    email = email.trim();
    password = password.trim();
    mfaCode = mfaCode?.trim();
    const mfaSignature = getCookie(config.get().auth.cookies.mfa.identifier)?.trim();
    const res = await jwApi(
      "dashboard-scope",
      "create",
      {
        input: {
          email,
          password,
          // ⚠️ Don't pass empty strings
          code: mfaCode || void 0,
          remember_mfa: mfaCode ? !!rememberMfa : void 0,
          mfa_signature: mfaSignature || void 0
        }
      },
      opts
    );
    if (res.status === 204) {
      deleteCookie(config.get().auth.cookies.mfa.identifier);
      throw new JwAuthError("MFA_REQUIRED");
    }
    if (res.data.authentication.mfa && typeof res.data.authentication.mfa === "string") {
      setCookie(config.get().auth.cookies.mfa.identifier, res.data.authentication.mfa, {
        "Max-Age": config.get().auth.cookies.mfa.ttl,
        Path: "/"
      });
    }
    deleteCookie(config.get().auth.cookies.authFromAdmin, {
      Path: "/"
    });
    return {
      obj: res.data,
      token: extractAuthToken(res.data)
    };
  });
};
var auth = new AuthGatewayManager();

export { JwAuthError, auth };
